<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-gray-900 bg-opacity-60 overflow-y-hidden h-full w-full flex items-center justify-center text-base"
  >
    <div class="bg-white rounded-lg shadow-xl w-9/12 h-[90vh] overflow-hidden">
      <div class="flex items-center justify-between p-4 border-b">
        <h2 class="text-2xl text-gray-800 font-semibold">Triage</h2>
        <button
          @click="closeModal"
          class="text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
      </div>

      <div class="p-6 flex max-h-[75vh] overflow-y-auto">
        <!-- Left column -->
        <div class="w-2/12 pr-6 border-r">
          <div class="flex items-center mb-4">
            <img
              src="@/assets/images/avatar.png"
              alt="Patient"
              class="w-12 h-12 rounded-full mr-4"
            />
            <div>
              <h3 class="font-semibold">{{ patientDetails.FullName }}</h3>
              <p class="text-sm text-gray-600">
                Patient ID: {{ patientDetails.RegistrationCode }}
              </p>
            </div>
          </div>

          <div class="space-y-2 mb-6 border-b-2">
            <p class="flex flex-col">
              <span class="font-semibold">Unique Code:</span>
              <span>{{ patientDetails.unique_code }}</span>
            </p>
            <p>
              <span class="font-semibold">Email:</span>
              {{ patientDetails.EmailAddress || "N/A" }}
            </p>
            <p>
              <span class="font-semibold">Phone:</span>
              {{ patientDetails.PhoneNumber || "N/A" }}
            </p>
            <p>
              <span class="font-semibold">D.O.B:</span> {{ patientDetails.Dob }}
            </p>
            <p>
              <span class="font-semibold">Gender:</span>
              {{ patientDetails.Gender }}
            </p>
            <p>
              <span class="font-semibold">Emergency Contact:</span>
              {{ patientDetails.emergency_contact?.Name || "N/A" }}
            </p>
            <p>
              <span class="font-semibold">Phone:</span>
              {{ patientDetails.emergency_contact?.PhoneNumber || "N/A" }}
            </p>
          </div>

          <!-- Vitals History section -->
          <h4 class="font-semibold py-2 border-b-2 border-t-2 text-center">
            Current Vitals
          </h4>
          <div class="grid grid-cols-3 gap-4">
            <!-- Temperature -->
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">🌡️</span>
              <p class="text-xs">Temperature</p>
              <p class="font-semibold">
                {{ patientDetails.latest_triage_vital?.temperature || "-" }}°C
              </p>
            </div>
            <!-- Heart Rate -->
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">❤️</span>
              <p class="text-xs">Heart Rate</p>
              <p class="font-semibold">
                {{ patientDetails.latest_triage_vital?.heart_rate || "-" }} BPM
              </p>
            </div>
            <!-- Respiratory Rate -->
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">🫁</span>
              <p class="text-xs">Resp. Rate</p>
              <p class="font-semibold">
                {{ patientDetails.latest_triage_vital?.respiratory_rate || "-" }}
              </p>
            </div>
            <!-- Blood Pressure -->
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">💉</span>
              <p class="text-xs">Blood Pressure</p>
              <p class="font-semibold">
                {{ patientDetails.latest_triage_vital?.blood_pressure_systolic || "-" }}/{{ patientDetails.latest_triage_vital?.blood_pressure_diastolic || "-" }}
              </p>
            </div>
            <!-- Weight -->
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">⚖️</span>
              <p class="text-xs">Weight</p>
              <p class="font-semibold">
                {{ patientDetails.latest_triage_vital?.weight || "-" }} kg
              </p>
            </div>
            <!-- Height -->
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">📏</span>
              <p class="text-xs">Height</p>
              <p class="font-semibold">
                {{ patientDetails.latest_triage_vital?.height || "-" }} cm
              </p>
            </div>
          </div>

          <!-- BMI and BSA -->
          <div class="mt-4 grid grid-cols-2 gap-4">
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">🧮</span>
              <p class="text-xs">BMI</p>
              <p class="font-semibold">
                {{ patientDetails.latest_triage_vital?.bmi || "-" }}KG/m2
              </p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">📊</span>
              <p class="text-xs">BSA</p>
              <p class="font-semibold">
                {{ patientDetails.latest_triage_vital?.bma || "-" }} m2
              </p>
            </div>
          </div>
        </div>

        <!-- Right column -->
        <div class="w-10/12 pl-6">
          <!-- Error Section -->
          <div
            v-if="errors.length"
            class="p-4 mb-4 border border-red-500 rounded-lg bg-red-50"
          >
            <h2 class="text-lg font-bold text-red-700 mb-2">
              Validation Errors
            </h2>
            <ul class="list-disc pl-5 text-red-600">
              <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
            </ul>
          </div>

          <!-- Tabs -->
          <div class="mb-4 border-b">
            <ul class="flex flex-wrap -mb-px">
              <li v-for="tab in tabs" :key="tab.name" class="mr-2">
                <a
                  href="#"
                  @click.prevent="currentTab = tab.component"
                  :class="[
                    'inline-block p-4 rounded-t-lg',
                    currentTab === tab.component
                      ? 'border-b-2 border-custom-yellow text-custom-blue'
                      : 'hover:text-gray-600 hover:border-gray-300',
                  ]"
                >
                  {{ tab.name }}
                </a>
              </li>
            </ul>
          </div>

          <!-- Tab content -->
          <keep-alive>
            <component
              v-if="currentTab === 'ConsultationPatientVitalsTab'"
              :state="state"
              :is="currentTab"
              :showHeadCircumference="showHeadCircumference"
              ref="vitalsTab"
              @vitals-submitted="handleVitalsSubmitted"
            >
            </component>
          </keep-alive>

          <keep-alive>
            <component
              v-if="currentTab === 'ConsultationTriageAssessmentTab'"
              :state="state"
              :is="currentTab"
              ref="assessmentTab"
              :gender="patientDetails.Gender"
              @assessment-submitted="handleAssessmentSubmitted"
            >
            </component>
          </keep-alive>

          <keep-alive>
            <component
              v-if="currentTab === 'ConsultationHistoryAllergyTab'"
              :is="currentTab"
              :patientId="submittedData.patient_id"
              ref="historyTab"
              @history-submitted="handleHistorySubmitted"
            >
            </component>
          </keep-alive>

          <keep-alive>
            <component
              v-if="currentTab === 'GynaeHistoryTab'"
              :is="currentTab"
              ref="gynaeTab"
              @gynae-history-submitted="handleGynaeHistorySubmitted"
            >
            </component>
          </keep-alive>

          <keep-alive>
            <component
              v-if="currentTab === 'ConsultationAdditionalInformationTab'"
              :is="currentTab"
              ref="additionalInfoTab"
              @additional-info-submitted="handleAdditionalInfoSubmitted"
              :doctors="doctors"
            >
            </component>
          </keep-alive>
        </div>
      </div>

      <!-- Tab-specific 'Next' button -->
      <div class="flex justify-end space-x-2 p-4 border-t">
        <button
          @click="handlePreviousTab"
          class="px-8 py-1 bg-gray-200 text-gray-800 rounded"
        >
          Previous
        </button>
        <button
          v-if="currentTab === 'ConsultationPatientVitalsTab'"
          @click="submitVitals"
          class="px-8 py-1 bg-custom-yellow text-white rounded"
        >
          Next
        </button>
        <button
          v-if="currentTab === 'ConsultationTriageAssessmentTab'"
          @click="submitAssessment"
          class="px-8 py-1 bg-custom-yellow text-white rounded"
        >
          Next
        </button>
        <button
          v-if="currentTab === 'ConsultationHistoryAllergyTab'"
          @click="submitHistory"
          class="px-8 py-1 bg-custom-yellow text-white rounded"
        >
          Next
        </button>
        <button
          v-if="currentTab === 'GynaeHistoryTab'"
          @click="submitGynaeHistory"
          class="px-8 py-1 bg-custom-yellow text-white rounded"
        >
          Next
        </button>
        <button
          v-if="currentTab === 'ConsultationAdditionalInformationTab'"
          :disabled="loading"
          @click="submitAdditionalInfo"
          class="px-8 py-1 bg-custom-yellow text-white rounded"
        >
          {{ loading ? 'Submitting..' : 'Submit' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ConsultationPatientVitalsTab from "@/views/tabs/triage_tabs/ConsultationPatientVitalsTab.vue";
import ConsultationTriageAssessmentTab from "@/views/tabs/triage_tabs/ConsultationTriageAssessmentTab.vue";
import ConsultationHistoryAllergyTab from "@/views/tabs/triage_tabs/ConsultationHistoryAllergyTab.vue"
import GynaeHistoryTab from "@/views/tabs/triage_tabs/GynaeHistoryTab"
import ConsultationAdditionalInformationTab from "@/views/tabs/triage_tabs/ConsultationAdditionalInformationTab.vue";
import apiClient from "@/services/api";

export default {
  name: "TriagePatientQueueModal",
  components: {
    ConsultationPatientVitalsTab,
    ConsultationTriageAssessmentTab,
    ConsultationHistoryAllergyTab,
    ConsultationAdditionalInformationTab,
    GynaeHistoryTab
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    visit: {
      type: Object,
      required: true,
    },
    state: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      currentTab: "ConsultationPatientVitalsTab",
      patientDetails: "",
      submittedData: {
        visit_id: "",
        visit_type: "",
        patient_id: "",
        vitals: null,
        assesment: null,
        history: null,
        additionalInfo: null,
      },
      showLoader: false,
      showHeadCircumference: false,
      errors: [],
      doctors: [],
    };
  },
  watch: {
    visit: {
      immediate: true,
      handler(newVisit) {
        if (newVisit) {
          this.patientDetails = newVisit.patient;
          this.submittedData.visit_id = newVisit.VisitId;
          this.submittedData.visit_type = newVisit.VisitType;
          this.submittedData.patient_id = newVisit.patient.PatientId;
          if (this.patientDetails.AgeType == 1 && this.patientDetails.Age < 3) {
            this.showHeadCircumference = true;
          }
          this.fetchDoctors(newVisit.VisitId);
        }
      },
    },
  },
  computed: {
    tabs() {
      const tabs = [
        { name: "Patient Vitals",         component: "ConsultationPatientVitalsTab" },
        { name: "Triage Assessment",      component: "ConsultationTriageAssessmentTab" },
        { name: "History & Allergy",      component: "ConsultationHistoryAllergyTab" },
      ]

      if (this.patientDetails?.Gender === "Female") {
        tabs.push({ name: "Gynaecological History", component: "GynaeHistoryTab" })
      }

      tabs.push({ name: "Additional Information", component: "ConsultationAdditionalInformationTab" })
      return tabs;
    }
  },
  methods: {
    handleMedicalHistory(patientId) {
      this.$router.push({
        name: "TriageMedicalHistoryPage",
        params: { patient_id: patientId },
      });
    },

    handleGynaeHistorySubmitted(gynaeData) {
      this.submittedData = { ...this.submittedData, ...gynaeData }
      this.moveToNextTab();
    },

    submitGynaeHistory() {
      if (this.$refs.gynaeTab) {
        this.$refs.gynaeTab.submitGynaeHistory()
      }
    },

    closeModal() {
      this.$emit("close");
    },
    handlePreviousTab() {
      const currentIndex = this.tabs.findIndex(
        (tab) => tab.component === this.currentTab
      );
      if (currentIndex > 0) {
        this.currentTab = this.tabs[currentIndex - 1].component;
      }
    },
    moveToNextTab() {
      const currentIndex = this.tabs.findIndex(
        (tab) => tab.component === this.currentTab
      );
      if (currentIndex < this.tabs.length - 1) {
        this.currentTab = this.tabs[currentIndex + 1].component;
      }
    },
    submitVitals() {
      if (this.$refs.vitalsTab) {
        this.$refs.vitalsTab.submitVitals();
      }
    },
    submitAssessment() {
      if (this.$refs.assessmentTab) {
        this.$refs.assessmentTab.submitAssessment();
      }
    },
    submitHistory() {
      if (this.$refs.historyTab) {
        this.$refs.historyTab.submitHistory();
      }
    },
    submitAdditionalInfo() {
      if (this.$refs.additionalInfoTab) {
        this.$refs.additionalInfoTab.submitAdditionalInfo();
      }
    },
    handleVitalsSubmitted(vitals) {
      console.log("Submitted Vitals:", vitals);
      this.submittedData.vitals = vitals;
      this.moveToNextTab();
    },
    handleAssessmentSubmitted(assessment) {
      console.log("Submitted Assessment:", assessment);
      this.submittedData.assesment = assessment;
      this.moveToNextTab();
    },
    handleHistorySubmitted(history) {
      console.log("Submitted History:", history);
      this.submittedData = { ...this.submittedData, ...history };
      console.log("form after history", this.submittedData);
      this.moveToNextTab();
    },
    handleAdditionalInfoSubmitted(additionalInfo) {
      console.log("Submitted Additional Info:", additionalInfo);
      this.submittedData = { ...this.submittedData, ...additionalInfo };
      console.log("formdata", this.submittedData);
      this.$emit("submit", this.submittedData);
    },
    async fetchDoctors(visitId) {
      try {
        this.showLoader = true;
        const response = await apiClient.fetchFacilityDoctors(
          visitId ? { visit_id: visitId } : {}
        );
        if (response.success) {
          this.doctors = response.data;
        }
      } catch (error) {
        this.showLoader = false;
        this.$toast.error("Failed to fetch doctors");
        console.log(error);
      } finally {
        this.showLoader = false;
      }
    },
    async handleSubmit() {
      try {
        this.showLoader = true;
        const response = await apiClient.saveTriage(this.submittedData);
        if (response.success) {
          this.showLoader = false;
          this.$toast.success("Patient triage saved successfully");
          this.$emit("reFetch");
          this.closeModal();
        }
      } catch (error) {
        this.showLoader = false;
        this.$toast.error("Failed to save triage data");
        console.log(error);
      }
    },
  },
};
</script>